/**
 * WordInputsArea — per-word typing inputs for sentence dictation.
 *
 * Shares the visual identity with the inline render block in
 * `pages/learning/DictationPage.tsx` (same CSS classes from
 * `DictationPage.css`), but lives as a reusable component so the custom
 * review sentence-dictation surface doesn't need to fork the markup.
 *
 * NOT a 1:1 drop-in for DictationPage's render today — DictationPage has
 * lemma-modal, dependency-graph, and audio-player wiring that's tangled
 * into the inline JSX. Migrating DictationPage to this component is a
 * separate, riskier refactor; we wanted the new sentence-dictation
 * surface unblocked first.
 *
 * Behavior parity that IS preserved:
 *   - Per-word inputs with active-word highlight
 *   - Tab / Shift+Tab navigation skipping punctuation-only tokens
 *   - Backspace at empty input → previous word
 *   - Enter → submit
 *   - Space → optional onPlayAudio callback (parent decides)
 *   - Auto-advance to next word on max-length reached
 *   - Punctuation-only tokens render as static spans
 *   - Show-result green/red coloring + per-word hint slot
 */
import React, { useEffect, useRef } from 'react';
import { isPunctuationOnly, isWordCorrectIgnoreCase, coerceInputCase } from '../../utils/wordComparison';
import './WordInputsArea.css';

export interface WordInputsAreaHintRenderProps {
  index: number;
  correctWord: string;
  userInput: string;
  isCorrect: boolean;
}

export interface WordInputsAreaProps {
  /** Words to render — one input per non-punctuation token. */
  correctWords: string[];
  /** Parent-owned input values. Length must match correctWords. */
  wordInputs: string[];
  /** Currently focused index (parent owns focus state for cross-block flow). */
  activeWordIndex: number;
  /** When true, switches inputs to disabled + shows green/red verdict. */
  showResult: boolean;
  /** Apply the .shake CSS animation (drives the existing keyframes). */
  shake?: boolean;

  // ── Required parent callbacks ────────────────────────────────────────
  onInputChange: (index: number, value: string) => void;
  onActiveChange: (index: number) => void;
  onSubmit: () => void;

  // ── Optional parent callbacks ────────────────────────────────────────
  /** Space key handler — typically toggles audio playback. */
  onPlayAudio?: () => void;

  // ── Per-word helpers (each has a sensible default) ───────────────────
  /** Word equality check. Default ignores case + diacritics + outer punctuation. */
  isWordCorrect?: (input: string, correctWord: string) => boolean;
  /** Display form of the correct word in the hint area. Default strips trailing punctuation. */
  normalizeWord?: (word: string) => string;
  /** Auto-advance threshold per word. Default uses normalized length. */
  getWordMaxLength?: (word: string) => number;
  /** Static punctuation displayed *before* the input (e.g. an opening quote). */
  getLeadingPunctuation?: (word: string) => string;
  /** Static punctuation displayed *after* the input (e.g. ", ?", ".", "—"). */
  getTrailingPunctuation?: (word: string) => string;

  // ── Slot ─────────────────────────────────────────────────────────────
  /** Custom hint renderer per-word — DictationPage wires lemma "?" buttons here. */
  renderWordHint?: (props: WordInputsAreaHintRenderProps) => React.ReactNode;

  // ── Externally controllable refs ─────────────────────────────────────
  /** Passed-up refs so the parent can imperatively focus a specific word. */
  inputRefs?: React.MutableRefObject<(HTMLInputElement | null)[]>;

  /** Optional className suffix appended to the container, for theming. */
  className?: string;
}

const defaultNormalizeWord = (word: string): string => {
  // Same logic as DictationPage.normalizeWord — strips diacritics +
  // outer punctuation but keeps abbreviations intact. Inlined here to
  // avoid pulling DictationPage's dependencies; if the two ever diverge,
  // hoist this into utils/wordComparison.
  if (!word) return '';
  const normalized = word
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/^[^\w']+/, '')
    .replace(/[""'']+$/, '');
  const abbrevPattern = /^[a-zA-Z](\.[a-zA-Z])+\.?[!?,;:]*$/;
  if (abbrevPattern.test(normalized)) {
    return normalized.replace(/[!?,;:]+$/, '');
  }
  return normalized.replace(/[.!?,;:-]+$/, '');
};

const defaultGetLeadingPunctuation = (word: string): string => {
  const match = word.match(/^[^\w']+/);
  return match ? match[0] : '';
};

const defaultGetTrailingPunctuation = (word: string): string => {
  const abbrevPattern = /^[a-zA-Z](\.[a-zA-Z])+\.?[!?,;:]*$/;
  if (abbrevPattern.test(word)) {
    const m = word.match(/[!?,;:]+$/);
    return m ? m[0] : '';
  }
  const match = word.match(/[.!?,;:\-""''「」『』)\]}>]+$/);
  return match ? match[0] : '';
};

const WordInputsArea: React.FC<WordInputsAreaProps> = ({
  correctWords,
  wordInputs,
  activeWordIndex,
  showResult,
  shake,
  onInputChange,
  onActiveChange,
  onSubmit,
  onPlayAudio,
  isWordCorrect,
  normalizeWord,
  getWordMaxLength,
  getLeadingPunctuation,
  getTrailingPunctuation,
  renderWordHint,
  inputRefs,
  className,
}) => {
  const internalRefs = useRef<(HTMLInputElement | null)[]>([]);
  const refs = inputRefs ?? internalRefs;

  const compareFn = isWordCorrect ?? isWordCorrectIgnoreCase;
  const normalizeFn = normalizeWord ?? defaultNormalizeWord;
  const leadingFn = getLeadingPunctuation ?? defaultGetLeadingPunctuation;
  const trailingFn = getTrailingPunctuation ?? defaultGetTrailingPunctuation;
  const maxLenFn = getWordMaxLength ?? ((w: string) => normalizeFn(w).length);

  // Reset ref array whenever the word list size changes — otherwise stale
  // refs from a previous sentence linger and Tab navigation jumps to
  // detached DOM nodes.
  useEffect(() => {
    if (refs.current.length !== correctWords.length) {
      refs.current = new Array(correctWords.length).fill(null);
    }
  }, [correctWords.length, refs]);

  // Focus follows `activeWordIndex`. Defer focus a tick — React commits
  // synchronously but the input may briefly be detached when the parent
  // swaps in a new sentence.
  useEffect(() => {
    const el = refs.current[activeWordIndex];
    if (el && document.activeElement !== el) {
      const id = window.setTimeout(() => el?.focus(), 50);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [activeWordIndex, refs]);

  const findNextInputable = (start: number, dir: 1 | -1): number => {
    let i = start + dir;
    while (i >= 0 && i < correctWords.length && isPunctuationOnly(correctWords[i])) {
      i += dir;
    }
    return i >= 0 && i < correctWords.length ? i : -1;
  };

  const handleChange = (index: number, raw: string) => {
    // Mirror DictationPage's casing rule: "I" stays uppercase, proper
    // nouns keep their leading capital. Without this users get harsh
    // "wrong" feedback on something they typed phonetically right.
    const cased = coerceInputCase(raw, correctWords[index] || '');
    const maxLen = maxLenFn(correctWords[index]);
    const truncated = cased.slice(0, maxLen);
    onInputChange(index, truncated);

    // Auto-advance to next inputable word on max-length reached. Defer so
    // the parent state has flushed before we trigger the focus change.
    if (truncated.length >= maxLen && index < correctWords.length - 1) {
      window.setTimeout(() => {
        const next = findNextInputable(index, 1);
        if (next >= 0) onActiveChange(next);
      }, 100);
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === ' ') {
      // Space toggles audio (if wired) — typing-mode dictation never
      // needs a literal space inside a word, so claiming the key is OK.
      e.preventDefault();
      onPlayAudio?.();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      const dir: 1 | -1 = e.shiftKey ? -1 : 1;
      const next = findNextInputable(index, dir);
      if (next >= 0) onActiveChange(next);
      return;
    }
    if (e.key === 'Backspace' && wordInputs[index] === '' && index > 0) {
      e.preventDefault();
      const prev = findNextInputable(index, -1);
      if (prev >= 0) onActiveChange(prev);
    }
  };

  return (
    <div className={`word-inputs-container${shake ? ' shake' : ''}${className ? ` ${className}` : ''}`}>
      {correctWords.map((word, idx) => {
        if (isPunctuationOnly(word)) {
          return (
            <span key={idx} className="word-punctuation-display">{word}</span>
          );
        }

        const userInput = wordInputs[idx] || '';
        const isEmpty = userInput === '';
        const isActive = idx === activeWordIndex;
        const isCorrect = compareFn(userInput, word);
        const charWidth = 14;
        const padding = 32;
        const minWidth = Math.max(normalizeFn(word).length * charWidth + padding, 60);
        const maxLen = maxLenFn(word);
        const leading = leadingFn(word);
        const trailing = trailingFn(word);
        const cls = [
          'word-input',
          isActive ? 'word-input--active' : '',
          showResult && isCorrect ? 'word-input--correct' : '',
          showResult && !isCorrect && !isEmpty ? 'word-input--incorrect' : '',
        ].filter(Boolean).join(' ');

        return (
          <div key={idx} className="word-input-wrapper">
            {leading && (
              <span className="word-punctuation word-punctuation--leading">{leading}</span>
            )}
            <input
              ref={(el) => { refs.current[idx] = el; }}
              type="text"
              className={cls}
              value={userInput}
              onChange={(e) => handleChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              onFocus={() => onActiveChange(idx)}
              disabled={showResult}
              maxLength={maxLen}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              inputMode="text"
              spellCheck={false}
              data-1p-ignore="true"
              data-lpignore="true"
              data-form-type="other"
              style={{ width: `${minWidth}px` }}
            />
            {trailing && (
              <span
                className={`word-punctuation${trailing === '-' ? ' word-punctuation--hyphen' : ''}`}
              >
                {trailing}
              </span>
            )}
            {showResult && !isCorrect && (
              <div className="word-hint">
                {renderWordHint
                  ? renderWordHint({ index: idx, correctWord: word, userInput, isCorrect })
                  : normalizeFn(word)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default WordInputsArea;
